import { downloadCSV } from '@/utils';
import {
  Autorenew as AutorenewIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  MaterialReactTable,
  MRT_TablePagination,
  useMaterialReactTable,
} from 'material-react-table';
import { useTelematicsBoxPolls } from './useTelematicsBoxPolls';
import { columns } from './utils';

export function PollHistory({ imei, date, onDateChange }) {
  const {
    query: { data: polls, isFetching, isLoading, refetch },
  } = useTelematicsBoxPolls(imei, date);

  function handleDownloadClick() {
    const headers = columns.map(({ header, id, accessorKey }) => ({
      label: header,
      key: id ?? accessorKey,
    }));

    downloadCSV(
      polls.map(({ position, ...poll }) => ({
        ...poll,
        latitude: position?.coordinates?.[1] ?? '',
        longitude: position?.coordinates?.[0] ?? '',
      })),
      `${imei}_${date.toISOString().split('T')[0]}.csv`,
      headers,
    );
  }

  function renderBottomToolbar() {
    return (
      <Stack
        direction="row"
        sx={{ pl: 1, justifyContent: 'space-between', alignItems: 'center' }}
      >
        <title>IR3 | Person Boundary Visits</title>
        <Box>
          {polls?.length > 0 && (
            <Tooltip title="Download polls">
              <IconButton size="small" onClick={handleDownloadClick}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <MRT_TablePagination table={table} />
      </Stack>
    );
  }

  const table = useMaterialReactTable({
    data: polls,
    columns,
    enablePagination: true,
    enableTopToolbar: false,
    enableBottomToolbar: true,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableSorting: true,
    defaultColumn: { size: 0 },
    initialState: { density: 'compact' },
    state: {
      isLoading: isLoading || isFetching,
    },
    muiTableBodyRowProps: {
      sx: { '&:last-child td, &:last-child th': { border: 0 } },
    },
    renderBottomToolbar,
  });

  function handleRefreshClick() {
    refetch();
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <DatePicker
          label="Date"
          value={date}
          onChange={onDateChange}
          slotProps={{ textField: { size: 'small' } }}
          views={['year', 'month', 'day']}
        />
        <Tooltip title={isLoading || isFetching ? 'Loading' : 'Refresh'}>
          <Box component="span">
            <IconButton
              onClick={handleRefreshClick}
              disabled={isLoading || isFetching}
              size="small"
            >
              <AutorenewIcon />
            </IconButton>
          </Box>
        </Tooltip>
      </Stack>
      <MaterialReactTable table={table} />
    </Stack>
  );
}
